import { LucideIcon, Database, FileText, FolderOpen, AlertCircle, Search, ClipboardList, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
}

export const EmptyState = ({
  icon: Icon = FolderOpen,
  title,
  description,
  action,
  secondaryAction,
}: EmptyStateProps) => {
  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-12 px-4">
        <Icon className="h-16 w-16 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold mb-2">{title}</h3>
        <p className="text-sm text-muted-foreground text-center mb-6 max-w-md">
          {description}
        </p>
        {action && (
          <div className="flex gap-2">
            <Button onClick={action.onClick}>{action.label}</Button>
            {secondaryAction && (
              <Button variant="outline" onClick={secondaryAction.onClick}>
                {secondaryAction.label}
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Predefined empty states for common scenarios
 */

export const EmptySourcesState = ({ onAdd }: { onAdd: () => void }) => (
  <EmptyState
    icon={Database}
    title="No data sources yet"
    description="Get started by connecting your first data source. You can connect to databases, cloud storage, or other data systems."
    action={{
      label: "Add Data Source",
      onClick: onAdd,
    }}
  />
);

export const EmptyDatasetsState = ({ onAdd, onBrowseSources }: { onAdd: () => void; onBrowseSources?: () => void }) => (
  <EmptyState
    icon={FileText}
    title="No datasets found"
    description="Create your first dataset by ingesting data from a connected source, or connect a new data source."
    action={{
      label: "Ingest Data",
      onClick: onAdd,
    }}
    secondaryAction={onBrowseSources ? {
      label: "Browse Sources",
      onClick: onBrowseSources,
    } : undefined}
  />
);

export const EmptyIngestionJobsState = ({ onUpload }: { onUpload: () => void }) => (
  <EmptyState
    icon={ClipboardList}
    title="No ingestion jobs"
    description="Start by uploading a file to ingest data into your lakehouse. Supported formats include CSV, Parquet, JSON, and more."
    action={{
      label: "Upload File",
      onClick: onUpload,
    }}
  />
);

export const EmptySearchResultsState = ({ onClear }: { onClear?: () => void }) => (
  <EmptyState
    icon={Search}
    title="No results found"
    description="Try adjusting your search terms or filters to find what you're looking for."
    action={onClear ? {
      label: "Clear Filters",
      onClick: onClear,
    } : undefined}
  />
);

export const EmptyCatalogState = () => (
  <EmptyState
    icon={FolderOpen}
    title="No catalog entries"
    description="The catalog will be populated as you ingest and register datasets in the system."
  />
);

export const EmptyQualityRulesState = ({ onCreate }: { onCreate: () => void }) => (
  <EmptyState
    icon={ClipboardList}
    title="No quality rules defined"
    description="Create quality rules to automatically validate your data during ingestion and ensure data quality standards."
    action={{
      label: "Create Rule",
      onClick: onCreate,
    }}
  />
);

/**
 * Error state component (different from empty state)
 */
interface ErrorStateProps {
  title?: string;
  message: string;
  onRetry?: () => void;
}

export const ErrorState = ({
  title = "Something went wrong",
  message,
  onRetry,
}: ErrorStateProps) => {
  return (
    <Card className="border-destructive">
      <CardContent className="flex flex-col items-center justify-center py-12 px-4">
        <AlertCircle className="h-16 w-16 text-destructive mb-4" />
        <h3 className="text-lg font-semibold mb-2">{title}</h3>
        <p className="text-sm text-muted-foreground text-center mb-6 max-w-md">
          {message}
        </p>
        {onRetry && (
          <Button onClick={onRetry} variant="outline">
            Try Again
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Access-denied state — shown when an authenticated user reaches a page they
 * lack permission for (e.g. a non-admin navigating directly to /users). Neutral
 * (not destructive) styling: this is an expected gate, not an error.
 */
interface AccessDeniedStateProps {
  title?: string;
  message?: string;
  onBack?: () => void;
}

export const AccessDeniedState = ({
  title = "Access denied",
  message = "You don't have access to this page. Contact an administrator if you believe this is a mistake.",
  onBack,
}: AccessDeniedStateProps) => {
  return (
    <Card>
      <CardContent className="flex flex-col items-center justify-center py-12 px-4">
        <ShieldAlert className="h-16 w-16 text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold mb-2">{title}</h3>
        <p className="text-sm text-muted-foreground text-center mb-6 max-w-md">
          {message}
        </p>
        {onBack && (
          <Button onClick={onBack} variant="outline">
            Go to Dashboard
          </Button>
        )}
      </CardContent>
    </Card>
  );
};
